import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Paper, Chip, Stack,
  CircularProgress, Alert, Divider, Avatar, Tooltip, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Card,
  LinearProgress, Collapse,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import BarChartIcon from '@mui/icons-material/BarChart';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import api from '../../services/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: ActionResult[];
  timestamp: Date;
  loading?: boolean;
  error?: boolean;
}

interface ActionResult {
  tool: string;
  input: any;
  result: { ok: boolean; data?: any; error?: string };
}

interface PlatformStats {
  active_customers: number;
  suspended_customers: number;
  active_carriers: number;
  active_rate_plans: number;
  total_rates: number;
  total_customer_balance: string;
  calls_24h: number;
  calls_7d: number;
  revenue_24h: string;
  routing_profiles: number;
  total_dids: number;
  active_fraud_alerts: number;
}

const QUICK_ACTIONS = [
  { label: 'Platform Stats', prompt: 'Show me platform statistics and health overview' },
  { label: 'Active Customers', prompt: 'List all active customers with their balance and rate plan' },
  { label: 'UK Rates', prompt: 'Show me all rates for UK (destination prefix 44)' },
  { label: 'Carriers', prompt: 'List all carriers' },
  { label: 'Low Balance Customers', prompt: 'Show customers with balance below $10' },
  { label: 'Fraud Alerts', prompt: 'Show active fraud alerts' },
  { label: 'Recent Calls', prompt: 'Show recent call records from the last 24 hours' },
  { label: 'Rate Plans', prompt: 'List all rate plans with their customer and rate counts' },
];

function toolLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const ActionCard: React.FC<{ action: ActionResult }> = ({ action }) => {
  const [expanded, setExpanded] = useState(false);
  const d = action.result.data;

  const renderData = () => {
    if (!d) return null;

    // Customers list
    if (d.customers && Array.isArray(d.customers)) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Company', 'Status', 'Balance', 'Plan', 'Billing'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {d.customers.map((c: any, i: number) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{c.name}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{c.company_name || '—'}</TableCell>
                <TableCell><Chip label={c.status} size="small" color={c.status === 'active' ? 'success' : 'error'} sx={{ fontSize: 10, height: 18 }} /></TableCell>
                <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>${parseFloat(c.balance || 0).toFixed(2)}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{c.rate_plan_name || '—'}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{c.billing_type}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // Rates list
    if (d.rates && Array.isArray(d.rates)) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Destination', 'Name', 'Plan', 'Buy Rate', 'Sell Rate', 'Margin', 'Carrier'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {d.rates.map((r: any, i: number) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>{r.destination}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{r.destination_name}</TableCell>
                <TableCell><Chip label={r.plan_name} size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 18 }} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#1565c0' }}>{parseFloat(r.buy_rate || 0).toFixed(6)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#2e7d32' }}>{parseFloat(r.sell_rate || 0).toFixed(6)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: parseFloat(r.margin) < 0 ? '#c62828' : '#2e7d32' }}>{parseFloat(r.margin || 0).toFixed(6)}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{r.carrier_name || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // Rate plans
    if (d.plans && Array.isArray(d.plans)) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Currency', 'Rates', 'Customers', 'Status'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {d.plans.map((p: any, i: number) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{p.name}</TableCell>
                <TableCell><Chip label={p.plan_type} size="small" color={p.plan_type === 'wholesale' ? 'warning' : p.plan_type === 'reseller' ? 'secondary' : 'primary'} sx={{ fontSize: 10, height: 18 }} /></TableCell>
                <TableCell sx={{ fontSize: 11 }}>{p.currency}</TableCell>
                <TableCell sx={{ fontSize: 11, textAlign: 'center' }}>{p.rate_count}</TableCell>
                <TableCell sx={{ fontSize: 11, textAlign: 'center' }}>{p.customer_count}</TableCell>
                <TableCell><Chip label={p.status} size="small" color={p.status === 'active' ? 'success' : 'default'} sx={{ fontSize: 10, height: 18 }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // Carriers
    if (d.carriers && Array.isArray(d.carriers)) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Host', 'Port', 'Transport', 'Status', 'Rules'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {d.carriers.map((c: any, i: number) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{c.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.host}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{c.port}</TableCell>
                <TableCell><Chip label={c.transport} size="small" sx={{ fontSize: 10, height: 18 }} /></TableCell>
                <TableCell><Chip label={c.status} size="small" color={c.status === 'active' ? 'success' : 'error'} sx={{ fontSize: 10, height: 18 }} /></TableCell>
                <TableCell sx={{ fontSize: 11, textAlign: 'center' }}>{c.routing_rule_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // CDRs
    if (d.cdrs && Array.isArray(d.cdrs)) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Destination', 'Duration', 'Status', 'Cost', 'Carrier', 'Time'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {d.cdrs.map((c: any, i: number) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>{c.destination}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{c.duration}s</TableCell>
                <TableCell><Chip label={c.disposition} size="small" color={c.disposition === 'ANSWERED' ? 'success' : 'error'} sx={{ fontSize: 10, height: 18 }} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>${parseFloat(c.cost || 0).toFixed(6)}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{c.carrier_name || '—'}</TableCell>
                <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(c.start_time).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // Platform stats
    if (d.active_customers !== undefined) {
      return (
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
          {[
            { label: 'Active Customers', value: d.active_customers, color: 'success.main' },
            { label: 'Suspended', value: d.suspended_customers, color: 'error.main' },
            { label: 'Carriers', value: d.active_carriers, color: 'info.main' },
            { label: 'Rate Plans', value: d.active_rate_plans, color: 'warning.main' },
            { label: 'Total Rates', value: d.total_rates?.toLocaleString(), color: 'text.primary' },
            { label: 'Customer Balance', value: '$' + parseFloat(d.total_customer_balance || 0).toFixed(2), color: '#2e7d32' },
            { label: 'Calls (24h)', value: d.calls_24h?.toLocaleString(), color: 'primary.main' },
            { label: 'Revenue (24h)', value: '$' + parseFloat(d.revenue_24h || 0).toFixed(2), color: '#2e7d32' },
            { label: 'Fraud Alerts', value: d.active_fraud_alerts, color: d.active_fraud_alerts > 0 ? 'error.main' : 'success.main' },
          ].map(s => (
            <Paper key={s.label} variant="outlined" sx={{ px: 2, py: 1, minWidth: 110, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">{s.label}</Typography>
              <Typography variant="h6" fontWeight={700} color={s.color} fontSize="1rem">{s.value}</Typography>
            </Paper>
          ))}
        </Stack>
      );
    }

    // Generic JSON viewer
    return (
      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        <pre style={{ fontSize: 11, margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(d, null, 2)}
        </pre>
      </Box>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1, borderRadius: 1,
        borderColor: action.result.ok ? 'success.light' : 'error.light',
        bgcolor: action.result.ok ? 'rgba(46,125,50,0.03)' : 'rgba(198,40,40,0.03)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(e => !e)}
      >
        {action.result.ok
          ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
          : <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
        }
        <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
          {toolLabel(action.tool)}
          {action.input && Object.keys(action.input).length > 0 && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({Object.entries(action.input).filter(([,v]) => v !== undefined && v !== null && v !== '').map(([k,v]) => `${k}: ${JSON.stringify(v)}`).join(', ')})
            </Typography>
          )}
        </Typography>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
      </Stack>
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1.5, overflowX: 'auto' }}>
          {action.result.ok ? renderData() : (
            <Typography variant="caption" color="error.main">{action.result.error}</Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const MessageBubble: React.FC<{ msg: Message }> = ({ msg }) => {
  const isUser = msg.role === 'user';

  if (msg.loading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <SmartToyIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', borderRadius: 2, px: 2, py: 1.5 }}>
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">Thinking…</Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      direction={isUser ? 'row-reverse' : 'row'}
      spacing={1.5}
      alignItems="flex-start"
      sx={{ mb: 2 }}
    >
      <Avatar
        sx={{
          bgcolor: isUser ? 'secondary.main' : 'primary.main',
          width: 32, height: 32, flexShrink: 0,
        }}
      >
        {isUser ? <PersonIcon sx={{ fontSize: 18 }} /> : <SmartToyIcon sx={{ fontSize: 18 }} />}
      </Avatar>

      <Box sx={{ maxWidth: '80%', minWidth: 100 }}>
        <Paper
          elevation={0}
          sx={{
            px: 2, py: 1.5,
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'white' : 'text.primary',
            borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {msg.content}
          </Typography>
        </Paper>

        {msg.actions && msg.actions.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {msg.actions.map((action, i) => (
              <ActionCard key={i} action={action} />
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', textAlign: isUser ? 'right' : 'left' }}>
          {msg.timestamp.toLocaleTimeString()}
        </Typography>
      </Box>
    </Stack>
  );
};

export const AiManagementPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: `👋 Hello! I'm your CallEssential AI Assistant.\n\nI can help you manage the entire platform through natural language. Try asking me:\n\n• "Show me all active customers"\n• "Update the UK rate to 0.0020"\n• "Add $50 balance to BT Corp"\n• "Assign destination 44 to customer NGN with BT-UK-Trunk as primary carrier"\n• "Show me calls from the last 24 hours"\n• "Create a new customer called ABC Telecom"\n\nWhat would you like to do?`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [apiConfigured, setApiConfigured] = useState<boolean | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/admin-ai/stats');
      setStats(res.data);
    } catch { }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: msg, timestamp: new Date() };
    const loadingMsg: Message = { id: 'loading', role: 'assistant', content: '', loading: true, timestamp: new Date() };

    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setLoading(true);
    scrollToBottom();

    // Build history for Claude (last 16 messages, excluding loading/welcome)
    const history = messages
      .filter(m => !m.loading && m.id !== 'welcome')
      .slice(-16)
      .map(m => ({
        role: m.role,
        content: m.content + (m.actions?.length ? '\n[Actions taken: ' + m.actions.map(a => a.tool).join(', ') + ']' : ''),
      }));

    try {
      const res = await api.post('/admin-ai/chat', { message: msg, history });
      const { response, actions, configured } = res.data;

      if (configured === false) setApiConfigured(false);
      else setApiConfigured(true);

      const aiMsg: Message = {
        id: Date.now().toString() + '_ai',
        role: 'assistant',
        content: response,
        actions: actions || [],
        timestamp: new Date(),
      };

      setMessages(prev => [...prev.filter(m => m.id !== 'loading'), aiMsg]);

      // Refresh stats after data-modifying actions
      if (actions?.some((a: any) => ['create_customer', 'add_balance', 'suspend_customer', 'activate_customer', 'update_rate'].includes(a.tool))) {
        loadStats();
      }
    } catch (e: any) {
      const errMsg: Message = {
        id: Date.now().toString() + '_err',
        role: 'assistant',
        content: '❌ ' + (e?.response?.data?.message || 'Request failed. Please try again.'),
        error: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev.filter(m => m.id !== 'loading'), errMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: 'Chat cleared. How can I help you?',
      timestamp: new Date(),
    }]);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
              <SmartToyIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>AI Management Console</Typography>
              <Typography variant="caption" color="text.secondary">Natural language control for CallEssential platform</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh stats">
              <IconButton size="small" onClick={loadStats} disabled={statsLoading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear conversation">
              <IconButton size="small" onClick={clearChat}>
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Platform stats bar */}
        {statsLoading && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
        {stats && !statsLoading && (
          <Stack direction="row" spacing={2} sx={{ mt: 1.5, overflowX: 'auto' }} flexWrap="nowrap">
            {[
              { icon: '👥', label: 'Customers', value: stats.active_customers + ' active' + (stats.suspended_customers > 0 ? ` · ${stats.suspended_customers} suspended` : '') },
              { icon: '📞', label: 'Calls (24h)', value: (stats.calls_24h || 0).toLocaleString() },
              { icon: '💰', label: 'Revenue (24h)', value: '$' + parseFloat(String(stats.revenue_24h || 0)).toFixed(2) },
              { icon: '🔀', label: 'Carriers', value: stats.active_carriers + ' active' },
              { icon: '📋', label: 'Rate Plans', value: stats.active_rate_plans + ' · ' + (stats.total_rates || 0).toLocaleString() + ' rates' },
              ...(stats.active_fraud_alerts > 0 ? [{ icon: '🚨', label: 'Fraud Alerts', value: stats.active_fraud_alerts + ' active' }] : []),
            ].map(s => (
              <Chip
                key={s.label}
                icon={<Typography sx={{ fontSize: 13 }}>{s.icon}</Typography>}
                label={<><strong>{s.label}:</strong> {s.value}</>}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 22, flexShrink: 0 }}
              />
            ))}
          </Stack>
        )}

        {apiConfigured === false && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            Anthropic API key not configured. Set <code>ANTHROPIC_API_KEY</code> in <code>/opt/callessential/backend/.env</code> and restart: <code>pm2 restart callessential-api</code>
          </Alert>
        )}
      </Box>

      {/* Quick actions */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }} flexWrap="nowrap">
          {QUICK_ACTIONS.map(qa => (
            <Chip
              key={qa.label}
              label={qa.label}
              size="small"
              onClick={() => sendMessage(qa.prompt)}
              disabled={loading}
              clickable
              sx={{ flexShrink: 0, fontSize: 11 }}
            />
          ))}
        </Stack>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {messages.map(msg => (
          <MessageBubble key={msg.id} msg={msg} />
        ))}
        <div ref={bottomRef} />
      </Box>

      {/* Input */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask me anything… e.g. 'Show all customers', 'Update UK sell rate to 0.002', 'Add $100 to BT Corp'"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'grey.50' },
            }}
          />
          <IconButton
            color="primary"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&:disabled': { bgcolor: 'grey.300', color: 'white' }, borderRadius: 2, p: 1.2 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          Press Enter to send · Shift+Enter for new line · Powered by Claude AI
        </Typography>
      </Box>
    </Box>
  );
};
